/** packages */

/** utils */
import { escapeSpecialCharacter, createElementFromString } from '~/utils/string'
import {
  getOrCreateContainer,
  setDatasetVisibility,
} from '~/utils/chart/customLegends/general'
import type {
  GetCategoryToggle,
  GetLabelElement,
  GetLegends,
  GetLegend,
  GetPriceLegend,
  GetLegendsByCategory,
  GetLegendElements,
  OnClickCategory,
  OnClickLegend,
  GetPriceLegends,
  Category,
  Legend,
} from '~/utils/chart/customLegends/definitions'

/** constants */
import {
  iconChecked,
  iconUnchecked,
  benchmarkSustainableIcon,
} from '~/constants/customLegendsIcons'
import { MARKET_SLUG } from '~/src/constants/markets'

/** types */
import type { TCommon, TGeneral, TRecord } from '~/src/types/common'

const LEGEND_MAPPINGS = {
  LCO: 'LCO Feedstock',
  LFP: 'LFP Feedstock',
  NCM: 'NCM Feedstock',
  'Tolling Fees': 'Graphitisation Tolling Fees',
}
const SUSTAINABILITY_ICON = `<span class="inline-table align-middle">${benchmarkSustainableIcon}</span>`

const { emit } = useEventBus()

export const onClickCategory: OnClickCategory = ({
  category,
  labels,
  datasets,
  chart,
}) => {
  category.value = !category.value
  category.priceType.forEach((priceType) => {
    priceType.value = category.value
  })

  labels
    .filter(({ datasetIndex }) => {
      const dataSetInfo = datasets[datasetIndex]
      const categoryName = dataSetInfo.category
        ? dataSetInfo.category.name
        : dataSetInfo.feedstock.name
      return categoryName === category.name
    })
    .forEach(({ datasetIndex }) =>
      chart.setDatasetVisibility(datasetIndex, category.value),
    )

  chart.update()
  setDatasetVisibility(chart)
}

export const onClickLegend: OnClickLegend = ({
  legend,
  labels,
  datasets,
  chart,
  options,
}) => {
  const index = legend.datasetIndex
  const category =
    legend.dataset.category?.name || legend.dataset.feedstock?.name

  chart.setDatasetVisibility(index, !chart.isDatasetVisible(index))
  chart.update()

  const newLabels = labels.map((element, i) => {
    if (index !== i) return element

    const hidden = !chart.isDatasetVisible(index)
    return { ...element, hidden }
  })

  const categoryValue = validateAllSelectOption({
    category,
    labels: newLabels,
    datasets,
  })

  const categoryFound = options.categories.find(
    (item: TRecord) => item.name === category,
  )

  categoryFound.value = categoryValue

  chart.update()
  setDatasetVisibility(chart)
}

export const getCategoryToggle: GetCategoryToggle = ({
  category,
  replaceLegend,
}) => {
  const isChecked = category.value
  const categoryBackgroundColor = isChecked ? '#FDBD00' : '#CACBD3'
  const iconBackground = isChecked ? iconChecked : iconUnchecked

  const categoryName = replaceLegend
    ? category.name.replace(
        /\b(LCO|LFP|NCM|Tolling(?:\s+Fees)?)\b/g,
        (match) => LEGEND_MAPPINGS[match],
      )
    : category.name

  const labelName =
    category.market === MARKET_SLUG.CATHODE
      ? category.seriesTitle
      : categoryName

  const input = isChecked
    ? `<input id="id--${category.name}" type="checkbox" class="peer sr-only" checked />`
    : `<input id="id--${category.name}" type="checkbox" class="peer sr-only" />`

  return createElementFromString(`
    <label for="id--${category.name}" class=" flex cursor-pointer items-center w-max gap-x-2">
      <div class=" relative">
        ${input}
        <div class="block rounded-full bg-grey-400" style="background-color: ${categoryBackgroundColor}; width: 44px; height: 24px">
          <div class="price-toggle-icon " style="justify-content: ${isChecked ? 'flex-start' : 'flex-end'}">
            ${iconBackground}
          </div>
        </div>
        <div class="price-toggle-circle">
        </div>
      </div>

      <div class="text-gray-700 font-semibold">
        ${labelName}
      </div>
    </label>
  `)
}

export const validateAllSelectOption = ({ category, labels, datasets }) => {
  const elements: TCommon[] = labels.filter((element: TCommon) => {
    const dataSetInfo: TGeneral = datasets[element.datasetIndex]
    const categoryName: string = dataSetInfo.category
      ? dataSetInfo.category.name
      : dataSetInfo.feedstock.name

    return categoryName === category
  })

  return elements.every((element: TCommon) => !element.hidden)
}

export const getLabelElement: GetLabelElement = ({
  priceType: _priceType,
  isSustainable,
  name,
}) => {
  if (isSustainable) {
    return `
      <p class="sustainability-legend" >
        <span class="align-middle">Benchmark Sustainable ${name}</span>
        ${SUSTAINABILITY_ICON}
      </p>
    `
  }

  return `
    <p class="flex items-center text-gray-900 font-normal text-xs">
      ${name}
    </p>
  `
}

export const getLegendElement: GetLegend = ({ legend, onClick }) => {
  const dataSetInfo = legend.dataset
  const priceType = dataSetInfo.priceType?.name

  if (!priceType) return

  const isSustainable = dataSetInfo.isSustainable

  const backgroundColor = !legend.hidden ? legend.strokeStyle : '#ffffff'
  const borderColor = !legend.hidden ? legend.strokeStyle : '#23252C'

  const name = escapeSpecialCharacter(legend.text.trim())
  const label = getLabelElement({ priceType, isSustainable, name })

  const element = createElementFromString(`
    <div class="flex items-center ml-2 flex-row gap-x-2.5 cursor-pointer" data-testid="legend">
      <input class="form-checkbox cursor-pointer border border-b focus:shadow-none focus:ring-0 focus:ring-offset-0" type="checkbox" style="background-color: ${backgroundColor}; border-color: ${borderColor}; border-width: 2px; border-radius: 4px;width: 18px; height: 18px;" ${!legend.hidden ? 'checked' : ''} />
      ${label}
    </div>
  `)

  element.onclick = () => onClick(legend)

  return element
}

export const getPriceLegend: GetPriceLegend = ({
  category,
  replaceLegend,
  onClick,
}) => {
  const principalContainer = createElementFromString(`
    <div class="flex flex-col gap-y-4" id="id-container--${category.name}">

    </div>
  `)

  const categoryElement = getCategoryToggle({
    category,
    replaceLegend,
  })
  categoryElement.onclick = () => onClick(category)

  principalContainer.appendChild(categoryElement)

  return principalContainer
}

export const getLegends: GetLegends = ({ labels, datasets }) => {
  return labels.map((label) => {
    const index = label.datasetIndex
    const data = datasets[index]
    const dataset = {
      category: data.category,
      feedstock: data.feedstock,
      priceType: data.priceType,
      isSustainable: data.isSustainable,
      seriesTitle: data.seriesTitle,
      market: data.market,
    }
    return { ...label, dataset }
  })
}

export const getLegendsByCategory: GetLegendsByCategory = ({
  legends,
  category,
}) => {
  return legends
    .filter(
      (legend) =>
        legend.dataset.category?.name === category.name ||
        legend.dataset.feedstock?.name === category.name,
    )
    .map((item) => {
      const order = category.priceType.findIndex(
        (priceType) => priceType.name === item.dataset.priceType.name,
      )
      return { ...item, order }
    })
    .sort((a, b) => a.order - b.order)
}

export const getLegendElements: GetLegendElements = ({
  legends,
  category,
  onClick,
}) => {
  const legendsByCategory = getLegendsByCategory({
    category,
    legends,
  })

  const hasThreeChild = legendsByCategory.length >= 3
  const classes = hasThreeChild ? 'grid-cols-1 lg:grid-cols-4' : 'grid-cols-1'
  const legendsContainer = createElementFromString(`
    <div class="grid gap-x-4 gap-y-3 ${classes}">
    </div>
  `)

  const legendElements = legendsByCategory.map((legend) => {
    return getLegendElement({ legend, onClick })
  })

  legendsContainer.append(...legendElements)

  return legendsContainer
}

export const getPriceLegends: GetPriceLegends = ({
  legends,
  categories,
  replaceLegend,
  onCategoryClick,
  onLegendClick,
}) => {
  return categories.map((category) => {
    const legendElements = getLegendElements({
      legends,
      category,
      onClick: onLegendClick,
    })

    const priceLegendElement = getPriceLegend({
      category,
      replaceLegend,
      onClick: onCategoryClick,
    })

    priceLegendElement.appendChild(legendElements)

    return priceLegendElement
  })
}

export const generatePriceLegends = {
  id: 'htmlLegend',
  afterUpdate(chart: TCommon, _: TCommon, options: TCommon) {
    const container = getOrCreateContainer(options.containerID, [
      'flex',
      'flex-col',
      'gap-y-7',
    ])

    while (container.firstChild) {
      container.firstChild.remove()
    }

    const labels = chart.options.plugins.legend.labels.generateLabels(chart)
    const datasets = chart.data.datasets
    const legends = getLegends({ labels, datasets })
    const categories = options.categories
    const replaceLegend = options.replaceLegend

    const onCategoryClick = (category: Category) => {
      onClickCategory({ chart, labels, datasets, category })
      emit('on-category-click', { category, labels, datasets, chart, options })
    }

    const onLegendClick = (legend: Legend) => {
      onClickLegend({ legend, labels, datasets, chart, options })
      emit('on-legend-click', { legend, labels, datasets, chart, options })
    }

    const priceLegends = getPriceLegends({
      legends,
      categories,
      replaceLegend,
      onCategoryClick,
      onLegendClick,
    })

    container.append(...priceLegends)
  },
}
